/**
 * Section 4 of the Credit Assessment Template 27.10.2026, Tirhelo Repayment & Conduct History, filled in
 * from Tirhelo's own records of the customer's earlier loans (procedure manual 2.2; plan phase 2).
 * Only what the records hold is written; how the client communicated and the conclusion stay the assessor's.
 */

export interface HistoryAccount {
  accountId?: string;
  accountNumber?: string;
  applicationId?: string;
  status?: string;
  disbursedAmount?: number;
  totalOutstanding?: number;
  disbursementDate?: string;
  maturityDate?: string;
  closedDate?: string;
  closureReason?: string;
}

export interface HistoryInstallment {
  installmentId?: string;
  sequenceNum?: number;
  principalDue?: number;
  interestDue?: number;
  feesDue?: number;
  dueDate?: string;
  originalDueDate?: string;
  status?: string;
  penaltiesDue?: number;
  penaltiesPaid?: number;
}

export interface HistoryTransaction {
  txnId?: string;
  txnType?: string;
  valueDate?: string;
  reversedTxnId?: string;
  allocations?: { bucket?: string; amountDec?: number }[];
}

export interface HistoryNote {
  outcome?: string;
  promiseStatus?: string;
  createdDate?: string;
  isActive?: boolean;
}

export interface AccountRecords {
  installments?: HistoryInstallment[];
  transactions?: HistoryTransaction[];
  notes?: HistoryNote[];
}

/** Accounts that never paid out are not earlier loans */
const NOT_A_LOAN = ['PENDING', 'CANCELLED'];
const OPEN = ['ACTIVE', 'IN_ARREARS', 'DEFAULTED'];
const DAY = 24 * 60 * 60 * 1000;

const rand = (n: number) => n.toLocaleString('en-ZA', { style: 'currency', currency: 'ZAR' });
const day = (s?: string) => (s ? Date.UTC(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10)) : NaN);
const longDate = (s: string) => new Date(day(s)).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const earlierLoans = (accounts: HistoryAccount[] | undefined, applicationId?: string) =>
  (accounts || []).filter(a => a && a.applicationId !== applicationId && !NOT_A_LOAN.includes(String(a.status)));

const SCHEDULED = ['PRINCIPAL', 'INTEREST', 'FEES'];
/** The schedule endpoint sends no instalment id, so an instalment is known by its number */
export const instalmentKey = (i: HistoryInstallment) => i.installmentId || `#${i.sequenceNum ?? ''}`;

/**
 * The day each instalment was paid in full. The ledger does not link a payment to an instalment: it applies
 * everything paid toward the schedule (payments and discounts, less their reversals; principal, interest and
 * fees, not default charges) to the instalments oldest first, skipping waived ones (PaymentServiceImpl
 * syncInstallmentActuals). Replaying the payments in value-date order the same way gives the day each
 * instalment's share was reached; when a reversal reopened it, the later day counts.
 */
export function paidDates(installments: HistoryInstallment[] = [], transactions: HistoryTransaction[] = []): Record<string, string> {
  const paying = new Set(transactions.filter(t => t.txnType === 'PAYMENT' || t.txnType === 'DISCOUNT').map(t => t.txnId));
  const steps = transactions
    .filter(t => t.valueDate && (paying.has(t.txnId) || (t.reversedTxnId && paying.has(t.reversedTxnId))))
    .sort((a, b) => String(a.valueDate).localeCompare(String(b.valueDate)))
    .map(t => ({ on: String(t.valueDate), paid: -(t.allocations || []).filter(a => SCHEDULED.includes(String(a.bucket))).reduce((s, a) => s + (Number(a.amountDec) || 0), 0) }));
  const out: Record<string, string> = {};
  let needed = 0;
  [...installments].sort((a, b) => (a.sequenceNum ?? 0) - (b.sequenceNum ?? 0)).forEach(i => {
    if (i.status === 'WAIVED') return;
    needed += (Number(i.principalDue) || 0) + (Number(i.interestDue) || 0) + (Number(i.feesDue) || 0);
    let running = 0;
    steps.forEach(s => {
      const before = running;
      running += s.paid;
      if (before < needed - 0.005 && running >= needed - 0.005) out[instalmentKey(i)] = s.on;
    });
  });
  return out;
}

export function tirheloHistory(
  accounts: HistoryAccount[], records: Record<string, AccountRecords | undefined>, today: Date = new Date(),
): Record<'previousFacilities' | 'repayment' | 'extensions' | 'conduct', string> | undefined {
  if (!accounts.length) return undefined;
  const now = iso(today);
  const name = (a: HistoryAccount) => a.accountNumber || 'Earlier loan';

  // Previous facilities and their value
  const total = accounts.reduce((s, a) => s + (Number(a.disbursedAmount) || 0), 0);
  const describe = (a: HistoryAccount) => {
    const parts = [`${name(a)} ${rand(Number(a.disbursedAmount) || 0)}`];
    if (a.disbursementDate) parts.push(`disbursed ${longDate(a.disbursementDate)}`);
    if (a.closedDate) parts.push(`${a.closureReason === 'EARLY_SETTLEMENT' ? 'settled early' : 'settled'} ${longDate(a.closedDate)}`);
    else if (a.status === 'WRITTEN_OFF') parts.push('written off');
    else if (OPEN.includes(String(a.status))) parts.push(`${a.status === 'ACTIVE' ? 'active' : a.status === 'IN_ARREARS' ? 'in arrears' : 'defaulted'}, ${rand(Number(a.totalOutstanding) || 0)} outstanding`);
    return parts.join(', ');
  };
  const previousFacilities = `${plural(accounts.length, 'earlier loan')}, ${rand(total)} in total: ${accounts.map(describe).join('; ')}.`;

  // Repayment performance: each instalment's paid day against the day agreed in the schedule
  let onTime = 0, waived = 0, partial = 0;
  const late: number[] = [], overdue: number[] = [];
  let moved = 0, chargesDue = 0, chargesPaid = 0;
  accounts.forEach(a => {
    const r = records[String(a.accountId)];
    const paid = paidDates(r?.installments, r?.transactions);
    (r?.installments || []).forEach(i => {
      const agreed = i.originalDueDate || i.dueDate;
      if (i.originalDueDate && i.dueDate && i.originalDueDate !== i.dueDate) moved++;
      chargesDue += Number(i.penaltiesDue) || 0;
      chargesPaid += Number(i.penaltiesPaid) || 0;
      if (i.status === 'WAIVED') { waived++; return; }
      if (i.status === 'PAID') {
        const on = paid[instalmentKey(i)];
        if (!on || !agreed) return;
        const days = Math.round((day(on) - day(agreed)) / DAY);
        if (days > 0) late.push(days); else onTime++;
        return;
      }
      if (i.status === 'PARTIALLY_PAID') partial++;
      if (agreed && agreed < now) overdue.push(Math.round((day(now) - day(agreed)) / DAY));
    });
  });
  const counted = onTime + late.length;
  const repayment: string[] = [];
  if (counted) {
    repayment.push(`${onTime} of ${plural(counted, 'paid instalment')} on or before the due date`);
    if (late.length) repayment.push(`${late.length} paid late (up to ${plural(Math.max(...late), 'day')}, ${Math.round(late.reduce((s, x) => s + x, 0) / late.length)} on average)`);
  }
  if (overdue.length) repayment.push(`${plural(overdue.length, 'instalment')} past due now (oldest ${plural(Math.max(...overdue), 'day')})`);
  if (partial) repayment.push(`${plural(partial, 'instalment')} partly paid`);
  if (waived) repayment.push(`${plural(waived, 'instalment')} waived`);

  // Extensions, late settlement and additional interest
  const extensions: string[] = [];
  if (moved) extensions.push(`due date moved on ${plural(moved, 'instalment')}`);
  if (chargesDue > 0) extensions.push(`default charges of ${rand(chargesDue)} raised, ${rand(chargesPaid)} paid`);
  accounts.forEach(a => {
    if (a.closedDate && a.maturityDate && a.closedDate > a.maturityDate) {
      extensions.push(`${name(a)} settled ${plural(Math.round((day(a.closedDate) - day(a.maturityDate)) / DAY), 'day')} after maturity`);
    } else if (!a.closedDate && a.maturityDate && a.maturityDate < now && OPEN.includes(String(a.status))) {
      extensions.push(`${name(a)} past maturity (${longDate(a.maturityDate)}) and not settled`);
    }
  });
  if (accounts.some(a => a.status === 'WRITTEN_OFF')) extensions.push('written off');

  // Communication and conduct: what the collection notes record
  const notes = accounts.flatMap(a => records[String(a.accountId)]?.notes || []).filter(n => n.isActive !== false);
  const promises = notes.filter(n => n.outcome === 'PROMISE_TO_PAY' && n.promiseStatus !== 'REPLACED');
  const count = (s: string) => promises.filter(n => n.promiseStatus === s).length;
  const conduct: string[] = [];
  if (promises.length) {
    conduct.push(`${plural(promises.length, 'promise')} to pay: ${[[count('KEPT'), 'kept'], [count('BROKEN'), 'broken'], [count('PENDING'), 'still open']]
      .filter(([n]) => n).map(([n, w]) => `${n} ${w}`).join(', ')}`);
  }
  const OUTCOME: Record<string, string> = { NO_ANSWER: 'no answer', DISPUTE: 'dispute', REFUSED_TO_PAY: 'refused to pay', WRONG_NUMBER: 'wrong number' };
  const difficult = Object.entries(OUTCOME).map(([k, w]) => [notes.filter(n => n.outcome === k).length, w] as const).filter(([n]) => n);
  const contacts = notes.filter(n => n.outcome);
  if (contacts.length) {
    const latest = contacts.map(n => String(n.createdDate || '')).sort().pop();
    conduct.push(`${plural(contacts.length, 'collection contact')} recorded${difficult.length ? ` (${difficult.map(([n, w]) => `${n} ${w}`).join(', ')})` : ''}${latest ? `, latest ${longDate(latest)}` : ''}`);
  }

  const sentence = (parts: string[], none: string) => (parts.length ? `${parts.join('; ')}.`.replace(/^./, c => c.toUpperCase()) : none);
  return {
    previousFacilities,
    repayment: sentence(repayment, 'No instalments fell due yet.'),
    extensions: sentence(extensions, 'None recorded: no moved due dates, default charges or late settlement.'),
    conduct: sentence(conduct, 'No collection contacts or promises to pay recorded.'),
  };
}
